import EnvironmentalFootprintIcon from "@/components/icons/EnvironmentalFootprintIcon";
import WaterUseIcon from "@/components/icons/WaterUseIcon";
import { BiodiversityIcon } from "@/components/icons/chapters/MetricGroupIcons";
import {
  formatEnvironmentalCost,
  formatMassWithUnit,
  formatScientific,
} from "@/pages/dashboard/utils/formatters";
import { ImpactMetric, RecipeImpactMetricKey } from "@/pages/dashboard/utils/types";

const RECIPE_MAIN_METRICS: Record<"environmental_cost" | "water_use" | "land_use", ImpactMetric> = {
  environmental_cost: {
    formatter: formatEnvironmentalCost,
    label: "Environmental cost",
    key: "environmental_cost",
    icon: <EnvironmentalFootprintIcon />,
    unit: "EUR",
    unitTooltip:
      "If Planet Earth had a bank account, they'd invoice you for this amount for using its resources. This is calculated by assigning monetary values(€) to all the individual environmental impact categories, i.e. climate change, ecosystem quality, material resources, pollution, toxicity and water use.",
    impactGroup: "Main impacts",
  },
  water_use: {
    formatter: formatScientific("m3"),
    label: "Water use",
    key: "water_use",
    icon: <WaterUseIcon />,
    unit: "m3",
    unitTooltip:
      "The impact of freshwater consumption across the product life cycle, including water used in farming, manufacturing, and processing. Focuses on how much water is withdrawn and not returned to the local ecosystem. ",
    impactGroup: "Main impacts",
  },
  land_use: {
    formatter: formatScientific("m2*a crop-Eq"),
    label: "Land use (agricultural)",
    key: "land_use",
    icon: <BiodiversityIcon />,
    unit: "m2*a crop-Eq",
    unitTooltip:
      "The impact of occupying or transforming land for activities like agriculture, forestry, mining, or infrastructure. Reflects how land use affects ecosystems, soil quality, and biodiversity over time.",
    impactGroup: "Main impacts",
  },
};

const RECIPE_BIODIVERSITY_METRICS: Record<
  | "ecosystems"
  | "terrestrial_ecosystems"
  | "marine_ecosystems"
  | "freshwater_ecosystems"
  | "toxicity_freshwater"
  | "toxicity_marine"
  | "toxicity_terrestrial"
  | "eutrophication_freshwater"
  | "eutrophication_marine"
  | "acidification"
  | "photochemical_oxidant_terrestrial",
  ImpactMetric
> = {
  ecosystems: {
    formatter: formatScientific("species · year"),
    label: "Biodiversity footprint (total)",
    key: "ecosystems",
    unit: "species · year",
    unitTooltip:
      "The long-term loss of species caused by ecosystem disruption throughout your product’s life cycle. ",
    impactGroup: "Biodiversity",
  },
  terrestrial_ecosystems: {
    formatter: formatScientific("species · year"),

    label: "Land footprint (total)",
    key: "terrestrial_ecosystems",
    unit: "species · year",
    unitTooltip:
      "Biodiversity loss on land caused by habitat disruption, deforestation, and land-use change throughout your product’s life cycle.",
    impactGroup: "Biodiversity",
  },
  marine_ecosystems: {
    formatter: formatScientific("species · year"),

    label: "Marine footprint (total)",
    key: "marine_ecosystems",
    unit: "species · year",
    unitTooltip:
      "Biodiversity loss in oceans and seas caused by pollution, overfishing, ocean acidification, and habitat destruction throughout your product’s life cycle.",
    impactGroup: "Biodiversity",
  },
  freshwater_ecosystems: {
    formatter: formatScientific("species · year"),

    label: "Freshwater footprint (total)",
    key: "freshwater_ecosystems",
    unit: "species · year",
    unitTooltip:
      "Biodiversity loss in rivers, lakes, and wetlands due to water use, contamination, and habitat alteration across your product’s life cycle.",
  },
  toxicity_freshwater: {
    formatter: formatMassWithUnit("1,4-DCB-Eq"),
    label: "Freshwater toxicity",
    key: "toxicity_freshwater",
    unit: "1,4-DCB-Eq",
    unitTooltip:
      "The impact on freshwater species from exposure to toxic substances, such as heavy metals and pesticides, released into rivers, lakes, and wetlands. ",
    impactGroup: "Biodiversity",
  },
  toxicity_marine: {
    formatter: formatMassWithUnit("1,4-DCB-Eq"),
    label: "Marine toxicity",
    key: "toxicity_marine",
    unit: "1,4-DCB-Eq",
    unitTooltip:
      "The impact on marine biodiversity from toxic pollutants entering oceans and seas, including persistent organic pollutants, oil spills, and industrial discharges. ",
    impactGroup: "Biodiversity",
  },
  toxicity_terrestrial: {
    formatter: formatMassWithUnit("1,4-DCB-Eq"),
    label: "Land toxicity",
    key: "toxicity_terrestrial",
    unit: "1,4-DCB-Eq",
    unitTooltip:
      "The impact on land-based species from toxic chemicals accumulating in soil, such as heavy metals, pesticides, and industrial waste.",
    impactGroup: "Biodiversity",
  },
  eutrophication_freshwater: {
    formatter: formatMassWithUnit("P-Eq"),
    label: "Freshwater pollution",
    key: "eutrophication_freshwater",
    unit: "P-Eq",
    unitTooltip:
      "Biodiversity loss in freshwater ecosystems caused by general pollution, including nutrient runoff, organic waste, and industrial discharges.",
    impactGroup: "Biodiversity",
  },
  eutrophication_marine: {
    formatter: formatMassWithUnit("N-Eq"),
    label: "Marine pollution",
    key: "eutrophication_marine",
    unit: "N-Eq",
    unitTooltip:
      "Biodiversity loss in marine ecosystems caused by pollution from plastic waste, oil, nutrients, and chemicals affecting marine life and habitats.",
    impactGroup: "Biodiversity",
  },
  acidification: {
    formatter: formatMassWithUnit("SO2-Eq"),
    label: "Land pollution",
    key: "acidification",
    unit: "SO2-Eq",
    unitTooltip:
      "Biodiversity loss on land caused by pollutants that degrade habitats and harm soil-dwelling organisms. Includes solid waste, chemicals, and contaminants.",
    impactGroup: "Biodiversity",
  },
  photochemical_oxidant_terrestrial: {
    formatter: formatMassWithUnit("NOx-Eq"),
    label: "Smog (ecosystems)",
    key: "photochemical_oxidant_terrestrial",
    unit: "NOx-Eq",
    unitTooltip:
      "The impact on biodiversity caused by ground-level ozone and other reactive air pollutants that damage vegetation and reduce ecosystem resilience. ",
    impactGroup: "Biodiversity",
  },
};

const RECIPE_HUMAN_HEALTH_METRICS: Record<
  | "human_health"
  | "human_toxicity_carcinogenic"
  | "human_toxicity_non_carcinogenic"
  | "ionising_radiation"
  | "ozone_depletion"
  | "particulate_matter"
  | "photochemical_oxidant_human_health",
  ImpactMetric
> = {
  human_health: {
    formatter: formatScientific("species · year"),
    label: "Human health footprint (total)",
    key: "human_health",
    unit: "DALY",
    unitTooltip:
      "The impact on human health from factors like air pollution, toxic chemicals, and contaminated water. ",
    impactGroup: "Human health",
  },
  human_toxicity_carcinogenic: {
    formatter: formatMassWithUnit("1,4-DCB-Eq"),
    label: "Human toxicity (carcinogenic)",
    key: "human_toxicity_carcinogenic",
    unit: "1,4-DCB-Eq",
    unitTooltip:
      "Potential human health impacts from exposure to substances that can cause cancer. Includes emissions of carcinogenic chemicals like certain heavy metals, solvents, and combustion by products.",
    impactGroup: "Human health",
  },
  human_toxicity_non_carcinogenic: {
    formatter: formatMassWithUnit("1,4-DCB-Eq"),
    label: "Human toxicity (non-carcinogenic)",
    key: "human_toxicity_non_carcinogenic",
    unit: "1,4-DCB-Eq",
    unitTooltip:
      "Potential human health impacts from substances that do not cause cancer but may lead to chronic or acute health effects. Includes exposure to pollutants like ammonia, fluoride, and non-carcinogenic heavy metals.",
    impactGroup: "Human health",
  },
  ionising_radiation: {
    formatter: formatScientific("kBq Co-60-Eq"),
    label: "Radiation exposure",
    key: "ionising_radiation",
    unit: "kBq Co-60-Eq",
    unitTooltip:
      "The impact on human health from exposure to ionising radiation during the product life cycle, such as emissions from energy production or material processing.",
    impactGroup: "Human health",
  },
  ozone_depletion: {
    formatter: formatMassWithUnit("CFC-11-Eq"),
    label: "Ozone depletion",
    key: "ozone_depletion",
    unit: "CFC-11-Eq",
    unitTooltip:
      "The impact on human health caused by the thinning of the ozone layer, which increases exposure to harmful UV radiation. Driven by emissions of substances like CFCs and halons.",
    impactGroup: "Human health",
  },
  particulate_matter: {
    formatter: formatMassWithUnit("PM2.5-Eq"),
    label: "Air pollution",
    key: "particulate_matter",
    unit: "PM2.5-Eq",
    unitTooltip:
      "The impact on human health from inhaling fine particulate matter (PM2.5), nitrogen oxides, and other air pollutants released during production, transport, or energy use.",
    impactGroup: "Human health",
  },
  photochemical_oxidant_human_health: {
    formatter: formatMassWithUnit("NOx-Eq"),
    label: "Smog (human health)",
    key: "photochemical_oxidant_human_health",
    unit: "NOx-Eq",
    unitTooltip:
      "The impact on human health from exposure to ground-level ozone formed by chemical reactions between sunlight and pollutants like VOCs and NOₓ.",
    impactGroup: "Human health",
  },
};

const RECIPE_RESOURCE_SCARCITY_METRICS: Record<
  "material_resources" | "energy_resources" | "resource_scarcity",
  ImpactMetric
> = {
  material_resources: {
    formatter: formatMassWithUnit("Cu-Eq"),
    label: "Mineral resource",
    key: "material_resources",
    unit: "Cu-Eq",
    unitTooltip:
      "The impact of extracting and depleting non-renewable mineral resources such as metals and rare earth elements. Reflects long-term availability risks. ",
    impactGroup: "Resource scarcity",
  },
  energy_resources: {
    formatter: formatMassWithUnit("oil-Eq"),
    label: "Fossil Fuel",
    key: "energy_resources",
    unit: "oil-Eq",
    unitTooltip:
      "The impact of extracting and consuming fossil energy sources like coal, oil, and natural gas. Reflects both depletion and associated environmental pressure. ",
    impactGroup: "Resource scarcity",
  },
  resource_scarcity: {
    formatter: formatEnvironmentalCost,
    label: "Resource scarcity footprint (total)",
    key: "resource_scarcity",
    unit: "€",
    unitTooltip:
      "The combined impact of depleting non-renewable resources, including both mineral resources and fossil fuels, across the product life cycle. Reflects the pressure placed on finite resource availability. ",
    impactGroup: "Resource scarcity",
  },
};

export const ALL_RECIPE_IMPACTS: Record<RecipeImpactMetricKey, ImpactMetric> = {
  ...RECIPE_MAIN_METRICS,
  ...RECIPE_BIODIVERSITY_METRICS,
  ...RECIPE_HUMAN_HEALTH_METRICS,
  ...RECIPE_RESOURCE_SCARCITY_METRICS,
};
